import React, { useState, useEffect, useContext } from 'react';
import { Container, TextField, Button, Select, MenuItem, Typography, Box, Grid } from '@mui/material';
import { getCountries, sortCountries } from "../utils";
import { getUserBilling, updateUserBilling } from "../API/User";
import { AlertContext } from "../App/App";

export const BillingPreferences = () => {
    const [companyName, setCompanyName] = useState('');
    const [billingEmail, setBillingEmail] = useState('');
    const [country, setCountry] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [state, setState] = useState('');
    const [businessTaxId, setBusinessTaxId] = useState('');
    const [vatNumber, setVatNumber] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [eikNumber, setEikNumber] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [representativeName, setRepresentativeName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setSystemAlert } = useContext(AlertContext);

    useEffect(() => {
        const loadBillingData = async () => {
            try {
                const response = await getUserBilling();

                if (response) {
                    const billingData = response;
                    setCompanyName(billingData.companyName || '');
                    setBillingEmail(billingData.billingEmail || '');
                    setCountry(billingData.country || '');
                    setAddressLine1(billingData.addressLine1 || '');
                    setAddressLine2(billingData.addressLine2 || '');
                    setCity(billingData.city || '');
                    setPostalCode(billingData.postalCode || '');
                    setState(billingData.state || '');
                    setBusinessTaxId(billingData.businessTaxId || '');
                    setVatNumber(billingData.vatNumber || '');
                    setGstNumber(billingData.gstNumber || '');
                    setEikNumber(billingData.eikNumber || '');
                    setPhoneNumber(billingData.phoneNumber || '');
                    setRepresentativeName(billingData.representativeName || '');
                }
            } catch (err) {
                console.error('Error loading billing information:', err);
                setError('Failed to load billing information');
            } finally {
                setLoading(false);
            }
        };

        loadBillingData();
    }, []);

    const handleSave = async () => {
        const billingInfo = {
            companyName,
            billingEmail,
            country,
            addressLine1,
            addressLine2,
            city,
            postalCode,
            state,
            businessTaxId,
            vatNumber,
            gstNumber,
            eikNumber,
            phoneNumber,
            representativeName,
        };

        try {
            const response = await updateUserBilling(billingInfo);
            if (response.status === 200) {
                setSystemAlert({ msg: `Billing details updated`, type: 'success' });
            } else {
                setSystemAlert({ msg: `Failed to update billing information.`, type: 'error' });
            }
        } catch (error) {
            setSystemAlert({ msg: `Failed to update billing information.`, type: 'error' });
        }
    };

    const countryCodes = getCountries();
    const regionNames = new Intl.DisplayNames(['en-US'], { type: 'region' });
    const sortedCountryCodes = sortCountries(regionNames, countryCodes);

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Container>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>Billing Details</Typography>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                    {/* Company and Email */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Company Name"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Billing Email"
                            value={billingEmail}
                            onChange={(e) => setBillingEmail(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Country and State */}
                    <Grid item xs={12} md={6}>
                        <Select
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem value="" disabled>Select Country</MenuItem>
                            {sortedCountryCodes.map((code) => (
                                <MenuItem key={code} value={code}>
                                    {regionNames.of(code)}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="State/Province/Region"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Address Lines */}
                    <Grid item xs={12}>
                        <TextField
                            label="Address Line 1"
                            value={addressLine1}
                            onChange={(e) => setAddressLine1(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Address Line 2 (Optional)"
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* City and Postal Code */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Postal Code"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Bulgarian EIK if country is Bulgaria */}
                    {country === 'BG' && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    label="ЕИК (Bulgarian Company ID)"
                                    value={eikNumber}
                                    onChange={(e) => setEikNumber(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Phone Number"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Representative Name (МОЛ)"
                                    value={representativeName}
                                    onChange={(e) => setRepresentativeName(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}



                    {/* Optional Tax Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">If you are a business tax registrant, please enter your business tax ID here</Typography>
                    </Grid>

                    {/* VAT and GST Numbers */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="VAT Number (Optional)"
                            value={vatNumber}
                            onChange={(e) => setVatNumber(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="GST Number (Optional)"
                            value={gstNumber}
                            onChange={(e) => setGstNumber(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Save Button */}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            fullWidth
                        >
                            Save Changes
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};